import delegateListener from '../modules/delegate-listener';
import StorageEvent from '../api/storage-event';
import DebounceCombiner from '../modules/debounce-combiner';
import ConfirmLeaving from '../modules/confirm-leaving';
import Item from '../api/item';

class ItemViewWarehouse {
  constructor() {
    this.stepStatusClasses = ['not_started', 'incomplete', 'complete'].map(status => `steps__step--${status}`);
    this.handoverConfirmMessage = window.HandoverConfirmMessage || 'confirm handover without permit?';
    this.removeConfirmMessage = window.RemoveConfirmMessage || 'confirm removing storage event?';

    //Store timers per field here
    this._throttleInputChangeTimers = {};

    this.confirmLeaving = new ConfirmLeaving();

    this.storageEventDebouncer = new DebounceCombiner({
      callback: (data) => {
        const promises = [];

        // Sends a PATCH request for each StorageEvent that was modified during the debounce timeout
        Object.entries(data).forEach(([eventId, eventData]) => {
          const event = new StorageEvent(eventId);
          promises.push(
            event.patch(eventData)
              .then(result => {
                if(Array.isArray(result.stepStatuses) && result.stepStatuses.length) {
                  this._updateWarehouseStepStatuses(result.stepStatuses, eventId);
                }
              })
          )
        });
        Promise.all(promises).then(() => {
          this.confirmLeaving.deactivate();
        });
      }
    });
  }

  init() {
    // Bail early if NOT on warehouse user's item page
    if(!document.getElementById('warehousestages')) return;

    if (document.querySelector('input[data-field], select[data-field], textarea[data-field]')) {
      const inputChangeHandler = this._handleInputChange.bind(this);

      const warehouseProcessContainer = document.getElementById('warehousestages');
      if(warehouseProcessContainer) {
        // change occurs only onblur, need it sooner (this is throttled)
        warehouseProcessContainer.addEventListener('input', delegateListener(
          'input[type="text"][data-field], input[type="number"][data-field], textarea[data-field]', inputChangeHandler
        ));

        warehouseProcessContainer.addEventListener('change', delegateListener(
          '.date-picker__input[data-field], input[type="checkbox"][data-field], input[type="number"][data-field], input[type="radio"][data-field], select[data-field]', inputChangeHandler
        ));
      }
    }

    document.body.addEventListener('click', delegateListener(
      '[data-modal-target="handoff-form"]',
      event => this.handleHandoffModalOpen(event)
    ));

    // Initialize StorageEvent removal buttons, when applicable
    if(document.querySelector('button[data-remove-storage-event]')) {
      document.body.addEventListener('click', delegateListener(
        'button[data-remove-storage-event]',
        event => this.handleRemoveEvent(event)
      ));
    }

    // Show a confirmation dialog, if user tries to handover the item,
    // when a handover permit has not been given.
    document.querySelectorAll('input[data-field="handoff_done"]')
      .forEach(this.initHandoffInput.bind(this));


    if(document.querySelector('.js-move-to-sjoberg-nurmijarvi')) {
      document.querySelector('.js-move-to-sjoberg-nurmijarvi').addEventListener('click', this._moveToSjobergNurmijarvi.bind(this));
    }
  }

  initHandoffInput(handoffInput) {
    // find the related handoff permit input
    const eventIdMatch = handoffInput.id.match(/handoff_done--(\d+)_0?$/);
    const eventId = eventIdMatch.length >= 2 ? eventIdMatch[1] : null;
    if(!eventId) return;
    const permitInput = document.querySelector(`[id="handoff_permit_date--${eventId}"]`);
    const handoffDateInput = document.querySelector(`#handoff_date--${eventId}`);
    if(!permitInput.value.length) {
      const confirmHandler = this.confirmPermitlessHandoff.bind(this);
      handoffInput.addEventListener('change', event => confirmHandler(eventId, handoffInput, event));
      handoffDateInput.addEventListener('change', event => confirmHandler(eventId, handoffInput, event));
    }
  }

  confirmPermitlessHandoff(eventId, checkbox, event) {
    if(
      event.target === checkbox && !checkbox.checked ||
      event.target !== checkbox && checkbox.checked
    ) {
      return;
    }
    // Maybe one day we'll have a modal here. Until then, a confirm will do.
    // eslint-disable-next-line no-alert
    const shouldContinue = confirm(this.handoverConfirmMessage);
    if(shouldContinue) return;
    event.stopPropagation();
    event.preventDefault();
    if(event.target === checkbox) {
      checkbox.checked = false;
    } else {
      // Special case for flatpickr, where the actual input is hidden and changed by proxy
      const flatpickr = event.target.parentNode.querySelector('.flatpickr-input:not([type=hidden])');
      flatpickr.value = '';
    }
    return true;
  }

  /**
   * Populates modal form input data, based on the StorageEvent that's button
   * was clicked.
   * @param {Event} event - The click event for the button that opens the modal
   */
  handleHandoffModalOpen(event) {
    const { keys, handoff_date } = this.getHandoffFormValues(event.delegateTarget);
    const keysInput = document.getElementById('keys');
    keysInput.value = keys;
    const dateInput = document.getElementById('handoff_date');
    dateInput.value = handoff_date;

    if(dateInput._flatpickr) {
      dateInput._flatpickr.setDate(handoff_date);
    }
  }

  handleRemoveEvent(event) {
    // eslint-disable-next-line no-alert
    const shouldContinue = confirm(this.removeConfirmMessage);
    if(!shouldContinue) return;
    const { delegateTarget } = event;
    const { removeStorageEvent } = delegateTarget.dataset;
    const storageEvent = new StorageEvent(removeStorageEvent);
    storageEvent.remove().then(response => {
      if(response.status === 'OK') {
        const eventContainer = document.querySelector(`[data-storage-event-id="${ removeStorageEvent }"]`);
        eventContainer && eventContainer.remove();
      }
    });
  }

  /**
   * Finds and returns values from the current StorageEvent and current Item.
   * @param {HTMLElement} buttonEl - The initiating button element
   * @returns {Object} - An object with the results for each field
   */
  getHandoffFormValues(buttonEl) {
    try {
      const keysEl = document.getElementById('basic-info-keys-warehouse');
      const keys = keysEl ? parseInt(keysEl.textContent) : 0;
      const date = buttonEl.closest('.steps__step').querySelector('[data-field="handoff_date"]').value;
      return {
        keys,
        handoff_date: date
      };
    } catch(e) {
      return { keys: null, handoff_date: null };
    }
  }


  /**
   * Queues a debounced PATCH request when a field is edited, adding
   * localized value(s) to the data, where applicable.
   *
   * @param {Event} event - The change or input event of an input field
   */
  _handleInputChange(event) {
    const target = event.delegateTarget || event.currentTarget;
    const field = target.getAttribute('data-field');
    const storageEventContainer = target.closest('[data-storage-event-id]');
    const storageEventId = parseInt(storageEventContainer.dataset.storageEventId);
    const checkboxesControllingDateInputs = {
      arrived_at_warehouse: 'date_arrived_at_warehouse',
      handoff_done: 'handoff_date',
    };

    this.confirmLeaving.activate();

    if(target.type === 'checkbox') {
      const checkboxes = target.parentElement.querySelectorAll('input[type="checkbox"]');
      // Boolean checkboxes
      if(checkboxes.length === 1) {

        const debouncerObject = {
          [storageEventId]: {
            [field]: target.checked,
            [field + '__localized']: target.checked ? 'Kyllä' : 'Ei'
          }
        };

        // date controlling checkboxes
        if( target.checked && checkboxesControllingDateInputs[target.dataset.field] ){
          const dateInput = document.querySelector(`[data-field="${checkboxesControllingDateInputs[target.dataset.field]}"]`);
          if( dateInput && ( !dateInput.value || dateInput.value === 'NAN') ){ // seems that empty date fields may have value 'NAN'
            dateInput._flatpickr.setDate('today');
            debouncerObject[storageEventId][checkboxesControllingDateInputs[target.dataset.field]] = dateInput.value;
          }
        }

        this.storageEventDebouncer.push(debouncerObject);
        return;
      }
      // Multi-choice checkboxes
      const checkedBoxes = Array.from(checkboxes).filter(c => c.checked);
      const values = checkedBoxes.map(c => c.value);
      const localized = checkedBoxes.map(c => {
        const label = document.querySelector(`label[for="${ c.id }"]`);
        return label ? label.innerText.trim() : null;
      });
      this.storageEventDebouncer.push({ [storageEventId]: { [field]: values, [field + '__localized']: localized }});
      return;
    }

    // Other fields
    this.storageEventDebouncer.push({ [storageEventId]: { [field]: target.value }})
  }

  /**
   * Updates the value of a single field of the storage event
   * @param {number} storageEventId - The pid of the storage event in question
   * @param {string} updatedData - The new value and localization
   */
  _updateFieldValue(storageEventId, updatedData) {
    const storageEvent = new StorageEvent(storageEventId);
    storageEvent.patch(updatedData).then(result => {
      if(Array.isArray(result.stepStatuses) && result.stepStatuses.length > 0) {
        this._updateWarehouseStepStatuses(result.stepStatuses, storageEventId);
      }
    })
  }

  _updateWarehouseStepStatuses(statuses, storageEventId) {
    statuses.forEach(({stepID, stepStatus}) => {
      const statusElement = document.querySelector(
        `[data-storage-event-id="${storageEventId}"] [data-step="${stepID}"]`
      )
      statusElement.classList.remove(...this.stepStatusClasses);
      statusElement.classList.add(`steps__step--${ stepStatus }`);
    });
  }

  _moveToSjobergNurmijarvi() {
    const shouldContinue = confirm('Siirrä kohde varastoon Sjöberg Nurmijärvi?');
    if(!shouldContinue) return;

    const item = new Item(window.ITEM_ID);

    const data = {
      toSjobergNurmijarvi: true,
    };

    item.updateLocationSjoberg(data).then(response => {

      if(response.status === 'OK') {
        window.location.href = '/';
      }
    });
  }
}

export default ItemViewWarehouse;
